import type { StaticImageData } from 'next/image'

import { cn } from '@/utilities/ui'
import React from 'react'

import type { MediaBlock as MediaBlockProps, Media } from '@/payload-types'

import { Media as MediaComponent } from '../../components/Media'
import { SectionWrapper } from '@/components/SectionWrapper'

type Props = MediaBlockProps & {
  breakout?: boolean
  className?: string
  enableGutter?: boolean
  imgClassName?: string
  staticImage?: StaticImageData
  disableInnerContainer?: boolean
  captionClassName?: string
  // Backward compatibility: support old 'media' prop
  media?: (string | null) | Media
}

export const MediaBlock: React.FC<Props> = (props) => {
  const {
    className,
    enableGutter = true,
    imgClassName,
    mediaType = 'image',
    image,
    video,
    size = 'container',
    settings,
    // Backward compatibility: support old 'media' prop
    media,
    staticImage,
    disableInnerContainer,
  } = props

  // Backward compatibility: if old 'media' prop exists, treat it as image
  const effectiveMediaType = media ? 'image' : mediaType
  const effectiveImage = image || media || null

  // Get media resource based on type
  const mediaResource =
    effectiveMediaType === 'image'
      ? effectiveImage
      : video?.videoFile && typeof video.videoFile === 'object'
        ? video.videoFile
        : null

  const videoUrl = effectiveMediaType === 'video' ? video?.videoUrl : undefined
  const posterImage =
    effectiveMediaType === 'video' && video?.posterImage && typeof video.posterImage === 'object'
      ? video.posterImage
      : null

  return (
    <SectionWrapper
      settings={settings}
      fullWidth={size === 'full'}
      className={className}
    >
      <div className="flex w-full justify-center">
        <div
          className={cn('w-full', {
            'max-w-full': size === 'full',
            'max-w-3xl': size === 'narrow',
            'max-w-6xl': size === 'container',
          })}
        >
          {(mediaResource || staticImage || (effectiveMediaType === 'video' && videoUrl)) && (
            <div className="relative w-full">
              {effectiveMediaType === 'image' ? (
                <MediaComponent
                  imgClassName={cn('w-full h-auto', imgClassName)}
                  resource={mediaResource || undefined}
                  src={staticImage}
                />
              ) : (
                <MediaComponent
                  resource={mediaResource || undefined}
                  videoUrl={videoUrl || undefined}
                  poster={posterImage || undefined}
                  videoClassName="w-full h-auto"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </SectionWrapper>
  )
}
